import { Component, ReactNode, ErrorInfo } from 'react';
import { Shield, RefreshCcw, AlertTriangle, Home, Trash2 } from 'lucide-react';

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

/**
 * Global Error Boundary — catches any uncaught render/module errors
 * (e.g. OAuth config failures, network issues, router mismatches)
 * and shows a friendly recovery screen instead of a blank page.
 *
 * Provides two recovery options:
 *  1. Reload Page — simple refresh
 *  2. Clear Cache & Go Home — clears localStorage/sessionStorage then redirects
 *     to root. This breaks crash loops caused by stale auth tokens or cached state.
 */
export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        console.error('[ErrorBoundary] Caught error:', error);
        console.error('[ErrorBoundary] Component stack:', info.componentStack);
    }

    handleReload = () => {
        window.location.reload();
    };

    /**
     * Clear all cached state (auth tokens, local data) and navigate home.
     * Use this if a simple reload doesn't fix the crash — stale tokens
     * can cause auth/routing loops that persist across refreshes.
     */
    handleClearAndHome = () => {
        try {
            localStorage.clear();
            sessionStorage.clear();
            // Clear IndexedDB used by Firebase Auth persistence
            const dbDeleteReq = indexedDB.deleteDatabase('firebaseLocalStorageDb');
            dbDeleteReq.onsuccess = () => {
                window.location.href = '/';
            };
            dbDeleteReq.onerror = () => {
                window.location.href = '/';
            };
        } catch {
            window.location.href = '/';
        }
    };

    render() {
        if (this.state.hasError) {
            const isAuthError =
                this.state.error?.message?.toLowerCase().includes('auth') ||
                this.state.error?.message?.toLowerCase().includes('oauth') ||
                this.state.error?.message?.toLowerCase().includes('unauthorized') ||
                this.state.error?.message?.toLowerCase().includes('router');

            return (
                <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6 font-sans">
                    <div className="max-w-md w-full text-center">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-red-500/10 border border-red-500/20 mb-6">
                            {isAuthError ? (
                                <Shield className="w-8 h-8 text-red-400" />
                            ) : (
                                <AlertTriangle className="w-8 h-8 text-red-400" />
                            )}
                        </div>

                        <h1 className="text-2xl font-bold text-white mb-2">
                            {isAuthError ? 'Session Error' : 'Something went wrong'}
                        </h1>

                        <p className="text-slate-400 text-sm leading-relaxed mb-6">
                            {isAuthError
                                ? 'Your session data may be stale or corrupted. Use "Clear Cache" below to reset your session and return home.'
                                : 'An unexpected error occurred. A simple reload usually fixes this. If it persists, clear the cache to start fresh.'}
                        </p>

                        {this.state.error && (
                            <details className="mb-6 text-left">
                                <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-300 transition-colors">
                                    Technical details
                                </summary>
                                <pre className="mt-2 p-3 bg-slate-900 border border-slate-800 rounded-xl text-xs text-red-400 overflow-x-auto whitespace-pre-wrap">
                                    {this.state.error.message}
                                </pre>
                            </details>
                        )}

                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <button
                                onClick={this.handleReload}
                                className="inline-flex items-center justify-center gap-2 bg-brand-primary hover:bg-orange-600 text-white font-bold px-6 py-3 rounded-xl transition-all shadow-lg shadow-orange-900/20"
                            >
                                <RefreshCcw className="w-4 h-4" />
                                Reload Page
                            </button>

                            <button
                                onClick={this.handleClearAndHome}
                                className="inline-flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 text-slate-200 font-semibold px-6 py-3 rounded-xl transition-all"
                            >
                                <Trash2 className="w-4 h-4 text-slate-400" />
                                Clear Cache & Go Home
                            </button>
                        </div>

                        <p className="text-xs text-slate-600 mt-4">
                            <Home className="w-3 h-3 inline mr-1" />
                            "Clear Cache" resets your session — you'll need to sign in again.
                        </p>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
